import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { ArrowLeft, RefreshCw, AlertTriangle, Clock, Inbox } from 'lucide-react';
import { getDeadlineFlag, DEADLINE_STYLES } from '../utils/deadlineFlag';

const STAGE_LABELS = {
  new: 'New', casting: 'Casting', stone_setting: 'Stone Setting',
  polishing: 'Polishing', rhodium: 'Rhodium', final_qc: 'Final QC', completed: 'Completed',
};

const fmtHours = (h) => {
  if (h < 1) return '<1h';
  if (h < 24) return `${Math.round(h)}h`;
  const days = Math.floor(h / 24);
  const hrs = Math.round(h % 24);
  return hrs > 0 ? `${days}d ${hrs}h` : `${days}d`;
};

const URGENCY_LABELS = {
  0: { label: 'Overdue', color: 'text-red-700', bg: 'bg-red-50 border-red-200' },
  1: { label: 'Due Today', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
  2: { label: 'Due Tomorrow', color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200' },
  3: { label: 'This Week', color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200' },
  4: { label: 'Later', color: 'text-gray-600', bg: 'bg-white border-gray-200' },
  5: { label: 'No Committed Date', color: 'text-gray-400', bg: 'bg-gray-50 border-dashed border-gray-300' },
};

const MyWorkQueue = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchQueue = async () => {
    setLoading(true);
    try {
      const res = await api.get('/my-work-queue');
      setData(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchQueue(); }, []);

  const summary = data?.summary || {};
  const orders = data?.orders || [];

  // Group by urgency bucket
  const grouped = {};
  orders.forEach(o => {
    let urg = o._computed?.urgency ?? 4;
    // Separate "no committed date" (days_left=999) from real "later" orders
    if (o._computed?.days_left === 999) urg = 5;
    if (!grouped[urg]) grouped[urg] = [];
    grouped[urg].push(o);
  });

  return (
    <div className="h-screen flex flex-col bg-gray-50" data-testid="my-work-queue-page">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg font-semibold">My Work Queue</h1>
              <p className="text-xs text-emerald-200">{summary.total || 0} active orders</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" className="text-white hover:bg-emerald-700" onClick={fetchQueue}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="px-4 pt-3">
        <div className="grid grid-cols-4 gap-2">
          <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-center">
            <p className="text-2xl font-bold text-red-700">{summary.overdue || 0}</p>
            <p className="text-[10px] text-red-600 font-semibold uppercase">Overdue</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-center">
            <p className="text-2xl font-bold text-amber-700">{summary.due_today || 0}</p>
            <p className="text-[10px] text-amber-600 font-semibold uppercase">Due Today</p>
          </div>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5 text-center">
            <p className="text-2xl font-bold text-orange-600">{summary.at_risk || 0}</p>
            <p className="text-[10px] text-orange-500 font-semibold uppercase">At Risk</p>
          </div>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2.5 text-center">
            <p className="text-2xl font-bold text-emerald-700">{summary.total || 0}</p>
            <p className="text-[10px] text-emerald-600 font-semibold uppercase">Total</p>
          </div>
        </div>
      </div>

      {/* Order List */}
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-4">
        {loading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-emerald-600" /></div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <Inbox className="w-14 h-14 mx-auto mb-3 text-gray-300" />
            <p className="font-semibold text-gray-600">Queue is clear</p>
            <p className="text-sm text-gray-400 mt-1">No active orders assigned to you</p>
          </div>
        ) : (
          <>
          {/* Today's Focus — Top 5 */}
          {orders.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-1.5 h-5 bg-emerald-500 rounded-full" />
                <p className="text-sm font-bold text-gray-900">Today's Focus</p>
                <span className="text-[10px] text-gray-400">Top {Math.min(5, orders.length)} priority</span>
              </div>
              <div className="space-y-2">
                {orders.slice(0, 5).map((order, idx) => {
                  const c = order._computed || {};
                  const flag = getDeadlineFlag(order);
                  return (
                    <div key={order._id}
                      onClick={() => navigate('/messenger', { state: { selectOrder: order._id } })}
                      className="rounded-lg border-2 border-emerald-300 bg-emerald-50/50 p-3 cursor-pointer hover:shadow-md transition-shadow"
                      data-testid={`focus-order-${idx}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-2.5">
                          <span className="w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{idx + 1}</span>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-sm text-gray-900">{order.order_number}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${DEADLINE_STYLES[flag.level]}`}>{flag.label}</span>
                              {c.is_blocked && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500 text-white">BLOCKED</span>}
                            </div>
                            <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                              <span className="px-1.5 py-0.5 bg-gray-100 rounded font-medium">{STAGE_LABELS[order.current_stage] || order.current_stage}</span>
                              <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{fmtHours(c.hours_in_stage)}</span>
                              {order.rework_count > 0 && <span className="text-amber-600 font-bold">↻ {order.rework_count}</span>}
                            </div>
                          </div>
                        </div>
                        <span className="text-xs text-gray-400">{order.title}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Full Queue grouped by urgency */}
          {Object.entries(grouped).sort((a, b) => Number(a[0]) - Number(b[0])).map(([urgency, groupOrders]) => {
            const urg = URGENCY_LABELS[urgency] || URGENCY_LABELS[4];
            return (
              <div key={urgency} className="mb-4">
                <p className={`text-xs font-bold uppercase tracking-wide mb-2 ${urg.color}`}>
                  {urg.label} ({groupOrders.length})
                </p>
                <div className="space-y-2">
                  {groupOrders.map(order => {
                    const c = order._computed || {};
                    const flag = getDeadlineFlag(order);
                    return (
                      <div
                        key={order._id}
                        onClick={() => navigate('/messenger', { state: { selectOrder: order._id } })}
                        className={`rounded-lg border p-3 cursor-pointer hover:shadow-md transition-shadow ${urg.bg}`}
                        data-testid={`queue-order-${order.order_number}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-sm text-gray-900">{order.order_number}</span>
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${DEADLINE_STYLES[flag.level]}`}>{flag.label}</span>
                              {c.is_blocked && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500 text-white">BLOCKED</span>}
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className="text-xs font-medium text-gray-700">{order.title}</span>
                              {order.main_group && <span className="text-[10px] text-gray-400">{order.main_group}</span>}
                            </div>
                            <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-500">
                              <span className="px-1.5 py-0.5 bg-gray-100 rounded font-medium">{STAGE_LABELS[order.current_stage] || order.current_stage}</span>
                              <span className="flex items-center gap-0.5"><Clock className="w-3 h-3" />{fmtHours(c.hours_in_stage)}</span>
                              {order.committed_delivery_date && <span>{order.committed_delivery_date}</span>}
                              {order.rework_count > 0 && <span className="text-amber-600 font-bold">↻ {order.rework_count}</span>}
                            </div>
                          </div>
                          {order.order_owner_name && (
                            <span className="text-[10px] text-emerald-600 font-medium flex-shrink-0 ml-2">{order.order_owner_name}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          </>
        )}
      </div>
    </div>
  );
};

export default MyWorkQueue;
